import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineCloudUpload } from 'react-icons/md';
import { FaArrowCircleUp, FaArrowAltCircleDown } from 'react-icons/fa';
import { COLORS } from '../styles/appStyles';

/**
 * 프로젝트 화면 컴포넌트
 * - 상단: PPT 파일 업로드 영역
 * - 하단: PPT 파일 그리드 뷰 (썸네일 + 제목 + 삭제 버튼)
 */
const ProjectView = ({
  pptItems,
  selectedPpt,
  onSelectPpt,
  onDeletePpt,
  onUpload,
  uploading,
  uploadProgress,
  uploadDone,
  uploadStatus,
  isMobile,
  showAlertModal,
  maxUploadSizeMb = 100,
  maxPptCount = 10
}) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingPpts, setDeletingPpts] = useState(new Set()); // 삭제 중인 PPT docId들
  const fileInputRef = useRef(null);

  // 정렬 상태
  const [sortBy, setSortBy] = useState('timestamp'); // 'timestamp' | 'filename' | 'totalSlides' | 'fileSize'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'

  // 새로 업로드된 PPT 추적 (세션 중에만 유지, 새로고침 시 초기화)
  const [newlyUploadedDocIds, setNewlyUploadedDocIds] = useState(new Set());
  const prevPptItemsRef = useRef(pptItems);

  // 업로드 완료 시 새로 추가된 PPT 감지
  useEffect(() => {
    if (uploadDone) {
      const prevDocIds = new Set(prevPptItemsRef.current.map(item => item.docId));
      const newDocIds = pptItems
        .filter(item => !prevDocIds.has(item.docId))
        .map(item => item.docId);

      if (newDocIds.length > 0) {
        setNewlyUploadedDocIds(prev => new Set([...prev, ...newDocIds]));
      }
    }
    prevPptItemsRef.current = pptItems;
  }, [pptItems, uploadDone]);


  // 정렬된 pptItems
  const sortedPptItems = [...pptItems].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'timestamp':
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        comparison = timeA - timeB;
        break;
      case 'filename':
        comparison = (a.filename || '').localeCompare(b.filename || '', 'ko');
        break;
      case 'totalSlides':
        comparison = (a.totalSlides || 0) - (b.totalSlides || 0);
        // 같으면 timestamp로 비교
        if (comparison === 0) {
          const timeA2 = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB2 = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          comparison = timeA2 - timeB2;
        }
        break;
      case 'fileSize':
        comparison = (a.fileSize || 0) - (b.fileSize || 0);
        // 같으면 timestamp로 비교
        if (comparison === 0) {
          const timeA3 = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB3 = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          comparison = timeA3 - timeB3;
        }
        break;
      default:
        comparison = 0;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // 정렬 옵션
  const sortOptions = [
    { field: 'timestamp', label: t('project.sort.latest') },
    { field: 'filename', label: t('project.sort.name') },
    { field: 'totalSlides', label: t('project.sort.length') },
    { field: 'fileSize', label: t('project.sort.size') }
  ];

  // 정렬 순서 토글
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // 파일 업로드 핸들러 - 10개 제한 체크 + pptx 확장자 검사
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // pptx 확장자 검사
    if (!file.name.toLowerCase().endsWith('.pptx')) {
      showAlertModal(t('project.pptxOnly'), 'warning');
      e.target.value = '';
      return;
    }

    if (pptItems.length >= maxPptCount) {
      showAlertModal(t('project.maxFilesWarning', { count: maxPptCount }), 'warning');
      e.target.value = '';
      return;
    }
    if (onUpload) {
      onUpload(e);
    }
  };

  // 드래그 앤 드롭 핸들러
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (uploading) return;

    // PPT 개수 제한 체크
    if (pptItems.length >= maxPptCount) {
      showAlertModal(t('project.maxFilesWarning', { count: maxPptCount }), 'warning');
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // .pptx 파일만 허용
      if (!file.name.toLowerCase().endsWith('.pptx')) {
        showAlertModal(t('project.pptxOnly'), 'warning');
        return;
      }
      // 가짜 이벤트 객체 생성하여 onUpload 호출
      const fakeEvent = {
        target: {
          files: [file],
          value: ''
        }
      };
      if (onUpload) {
        onUpload(fakeEvent);
      }
    }
  };

  // PPT 클릭 핸들러
  const handlePptClick = (docId) => {
    // 툴팁 제거
    setNewlyUploadedDocIds(prev => {
      const next = new Set(prev);
      next.delete(docId);
      return next;
    });

    if (onSelectPpt) {
      onSelectPpt(docId);
    }
  };

  // 삭제 버튼 클릭 핸들러 (docId로 삭제) - 동시 다중 삭제 지원
  const handleDeleteClick = async (e, docId) => {
    e.stopPropagation(); // 부모 클릭 이벤트 방지

    // 이미 삭제 중인 파일은 무시
    if (deletingPpts.has(docId)) return;

    if (onDeletePpt) {
      setDeletingPpts(prev => new Set([...prev, docId]));
      try {
        await onDeletePpt(docId);
      } finally {
        setDeletingPpts(prev => {
          const next = new Set(prev);
          next.delete(docId);
          return next;
        });
      }
    }
  };

  const styles = {
    container: {
      flex: 1,
      padding: isMobile ? '16px' : '32px',
      paddingTop: isMobile ? '20px' : '55px',
      backgroundColor: COLORS.background,
      overflowY: 'auto',
      minHeight: 'calc(100vh - 120px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },

    // 업로드 영역 - 가로 36%, 세로 80% 축소
    uploadArea: {
      backgroundColor: isDragOver ? COLORS.primaryLight : (pptItems.length === 0 ? 'rgba(26, 98, 234, 0.05)' : COLORS.backgroundTertiary),
      border: `2px dashed ${uploading || isDragOver || pptItems.length === 0 ? COLORS.primary : COLORS.border}`,
      borderRadius: '16px',
      padding: isMobile ? '24px 12px' : '38px 26px',
      textAlign: 'center',
      cursor: uploading ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      marginBottom: '32px',
      position: 'relative',
      overflow: 'hidden',
      width: isMobile ? '85%' : '36%',
      maxWidth: '500px',
      ...(pptItems.length === 0 && !uploading && {
        animation: 'uploadPulse 2s ease-in-out infinite',
        boxShadow: '0 0 20px rgba(26, 98, 234, 0.2)'
      })
    },

    uploadIcon: {
      width: '76px',
      height: '76px',
      marginBottom: '12px'
    },

    uploadTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: COLORS.textPrimary,
      margin: '0 0 8px 0'
    },

    uploadSizeInfo: {
      fontSize: '13px',
      color: '#6B7280',
      margin: '0 0 4px 0'
    },

    uploadSubtitle: {
      fontSize: '14px',
      color: COLORS.textSecondary,
      margin: 0
    },

    // 업로드 진행 상태
    progressBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: '4px',
      backgroundColor: COLORS.primary,
      transition: 'width 0.3s ease',
      width: `${uploadProgress}%`
    },

    progressText: {
      marginTop: '16px',
      fontSize: '14px',
      color: COLORS.primary,
      fontWeight: '500'
    },

    // 파일 목록 섹션 - 좌우 패딩
    fileListSection: {
      backgroundColor: COLORS.background,
      borderRadius: '16px',
      padding: isMobile ? '5vh 4vw' : '24px 60px',
      boxShadow: COLORS.shadow,
      width: '100%',
      boxSizing: 'border-box'
    },

    sectionTitle: {
      fontSize: isMobile ? '17px' : '24px',
      fontWeight: '600',
      color: COLORS.textPrimary,
      margin: '0',
      padding: '0 0 32px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },

    // 그리드 레이아웃 - 카드 크기 축소
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '32px'
    },

    // PPT 카드 - 크기 축소
    pptCard: (isSelected) => ({
      backgroundColor: isSelected ? COLORS.primaryLight : COLORS.backgroundSecondary,
      border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
      borderRadius: '10px',
      padding: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '180px'
    }),

    // 선택 유도 툴팁
    selectTooltip: {
      position: 'absolute',
      top: '-32px',
      right: '-20px',
      backgroundColor: COLORS.primary,
      color: 'white',
      padding: '6px 10px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      boxShadow: '0 2px 8px rgba(26, 98, 234, 0.3)',
      zIndex: 10,
      animation: 'tooltipPulse 2s ease-in-out infinite'
    },

    // 툴팁 화살표
    tooltipArrow: {
      position: 'absolute',
      bottom: '-6px',
      right: '20px',
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderTop: `6px solid ${COLORS.primary}`
    },

    // 썸네일 이미지
    thumbnail: {
      width: '100%',
      aspectRatio: '16/9',
      objectFit: 'cover',
      borderRadius: '6px',
      backgroundColor: COLORS.backgroundTertiary,
      marginBottom: '16px'
    },

    // PPT 제목 - 최대 2줄, 고정 높이
    pptTitle: {
      fontSize: '14px',
      fontWeight: '500',
      color: COLORS.textPrimary,
      textAlign: 'center',
      width: '100%',
      lineHeight: '1.4',
      height: '40px',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      wordBreak: 'break-word'
    },

    // 삭제 버튼 - 고정 위치
    deleteButton: {
      backgroundColor: COLORS.error,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 20px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      opacity: 0.9,
      marginTop: 'auto'
    },

    // 빈 상태
    emptyState: {
      textAlign: 'center',
      padding: '48px 16px',
      color: COLORS.textTertiary
    },

    emptyIcon: {
      fontSize: '64px',
      marginBottom: '16px',
      opacity: 0.5
    },

    emptyText: {
      fontSize: '16px',
      margin: 0
    },

    // 숨겨진 파일 인풋
    hiddenInput: {
      display: 'none'
    }
  };

  return (
    <div style={styles.container}>
      {/* 스피너 및 툴팁 애니메이션 */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes tooltipPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.9; }
          }
          @keyframes uploadPulse {
            0%, 100% {
              box-shadow: 0 0 20px rgba(26, 98, 234, 0.2);
              border-color: #1A62EA;
            }
            50% {
              box-shadow: 0 0 30px rgba(26, 98, 234, 0.4);
              border-color: #1A62EA;
            }
          }
        `}
      </style>
      {/* 업로드 영역 - 드래그 앤 드롭 지원 */}
      <div
        style={styles.uploadArea}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={(e) => {
          if (uploading) return;
          if (pptItems.length >= maxPptCount) {
            e.preventDefault();
            showAlertModal(t('project.maxFilesWarning', { count: maxPptCount }), 'warning');
            return;
          }
          fileInputRef.current?.click();
        }}
      >
        <input
          type="file"
          accept=".pptx"
          onChange={handleFileChange}
          disabled={uploading}
          style={styles.hiddenInput}
          ref={fileInputRef}
        />

        {uploading ? (
          <>
            <MdOutlineCloudUpload style={{ fontSize: '48px', color: '#000000' }} />
            <h3 style={styles.uploadTitle}>{t('project.uploading')}</h3>
            <p style={{ ...styles.uploadSubtitle, fontSize: '16px' }}>{uploadStatus || t('project.uploadingDesc')}</p>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #1A62EA',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginTop: '16px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }} />
          </>
        ) : uploadDone ? (
          <>
            <MdOutlineCloudUpload style={{ fontSize: '48px', color: '#000000' }} />
            <h3 style={styles.uploadTitle}>{t('project.uploadComplete')}</h3>
            <p style={styles.uploadSubtitle}>{t('project.uploadCompleteDesc')}</p>
          </>
        ) : (
          <>
            <MdOutlineCloudUpload style={{ fontSize: '48px', color: '#000000' }} />
            <h3 style={styles.uploadTitle}>{t('project.uploadTitle')}</h3>
            <p style={styles.uploadSizeInfo}>{t('project.uploadSubtitle', { size: maxUploadSizeMb })}</p>
            <p style={styles.uploadSubtitle}>{t('project.dragDrop')}</p>
          </>
        )}
      </div>

      {/* 파일 목록 */}
      <div style={styles.fileListSection}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: isMobile ? '4px' : '16px'
        }}>
          <h3 style={{ ...styles.sectionTitle, margin: 0 }}>
            <span>📁</span>
            {t('project.myFiles', { count: pptItems.length, max: maxPptCount })}
          </h3>
          {/* 정렬 드롭다운 + 화살표 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                background: '#ffffff',
                color: '#374151',
                fontSize: '17px',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {sortOptions.map(opt => (
                <option key={opt.field} value={opt.field}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={toggleSortOrder}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                border: 'none',
                borderRadius: '6px',
                background: 'transparent',
                color: '#1A62EA',
                cursor: 'pointer',
                padding: 0
              }}
              title={sortOrder === 'asc' ? t('project.sort.ascending') : t('project.sort.descending')}
            >
              {sortOrder === 'asc' ? (
                <FaArrowCircleUp style={{ fontSize: '20px' }} />
              ) : (
                <FaArrowAltCircleDown style={{ fontSize: '20px' }} />
              )}
            </button>
          </div>
        </div>

        {pptItems.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📄</div>
            <p style={styles.emptyText}>{t('project.noFiles')}</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {sortedPptItems.map((item) => {
              const isSelected = selectedPpt === item.docId;
              const thumbnailUrl = item.smallThumbnailUrl || '/ddalkkakcheck_img.png';

              return (
                <div
                  key={item.docId}
                  style={styles.pptCard(isSelected)}
                  onClick={() => handlePptClick(item.docId)}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = COLORS.primary;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = COLORS.border;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {/* 선택 유도 툴팁 - 새로 업로드된 PPT에만 표시 */}
                  {newlyUploadedDocIds.has(item.docId) && (
                    <div style={styles.selectTooltip}>
                      {t('project.selectPptTooltip')}
                      <div style={styles.tooltipArrow} />
                    </div>
                  )}

                  {/* 썸네일 이미지 - small_thumbnail_url이 없으면 기본 이미지 사용 */}
                  <img
                    src={thumbnailUrl}
                    alt={item.filename}
                    style={styles.thumbnail}
                    onError={(e) => {
                      e.target.src = '/ddalkkakcheck_img.png';
                    }}
                  />

                  {/* PPT 제목 */}
                  <div style={styles.pptTitle} title={item.filename}>
                    {item.filename}
                  </div>

                  {/* 삭제 버튼 */}
                  <button
                    style={{
                      ...styles.deleteButton,
                      ...(deletingPpts.has(item.docId) && {
                        opacity: 0.6,
                        cursor: 'not-allowed'
                      })
                    }}
                    onClick={(e) => handleDeleteClick(e, item.docId)}
                    disabled={deletingPpts.has(item.docId)}
                    onMouseEnter={(e) => {
                      if (!deletingPpts.has(item.docId)) {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!deletingPpts.has(item.docId)) {
                        e.currentTarget.style.opacity = '0.9';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {deletingPpts.has(item.docId) ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid white',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        {t('common.deleting')}
                      </span>
                    ) : t('common.delete')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectView;
